import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Cancel, CheckCircle } from "@mui/icons-material";
import Textfield from "../Textfield";
import {
  login,
  register_free,
  register_professional,
  register_team,
} from "../../url";
import { useNavigate } from "react-router-dom";
import pricing_plans from "../../Assets/pricing_plans.png";

// Shared styles for better consistency and maintainability
const styles = {
  container: {
    textAlign: "center",
  },
  heroTitle: {
    fontWeight: "bold",
    fontSize: { xs: "2.5rem", md: "4.5rem" },
    color: "#1662A7",
    fontFamily: "Roboto",
    textShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: { xs: "1rem", md: "1.8rem" },
    color: "#1662A7",
    fontWeight: "bold",
    textTransform: "uppercase",
    lineHeight: 1.4,
    mb: 2,
  },
  highlight: {
    color: "#25B2E8",
    fontWeight: "bold",
  },
  winText: {
    fontSize: { xs: "1.2rem", md: "2.2rem" },
    color: "#25B2E8",
    fontWeight: "bold",
    mb: 4,
  },
  noticeBox: {
    backgroundColor: "#F3F4F6",
    border: "1px solid #B8C2CC",
    borderRadius: "12px",
    p: 3,
    maxWidth: "600px",
    mx: "auto",
    mb: 4,
  },
  toggleButton: {
    height: { xs: "45px", md: "50px" },
    borderRadius: "12px",
    fontSize: "1.1rem",
    fontWeight: "medium",
    textTransform: "capitalize",
    width: { xs: "120px", md: "160px" },
    transition: "all 0.3s ease",
  },
  tableContainer: {
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    mb: 4,
  },
  tableHeader: {
    backgroundColor: "#F8F9FA",
    color: "#25B2E8",
    fontWeight: "bold",
    fontSize: { xs: "1.1rem", md: "1.4rem" },
    py: 2,
  },
  tableCell: {
    borderRight: "1px solid #e0e0e0",
    padding: "16px",
    "&:last-child": {
      borderRight: "none",
    },
  },
  mobileCard: {
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    mb: 3,
    overflow: "hidden",
    boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
  },
  actionButton: {
    backgroundColor: "#25B2E8",
    color: "white",
    borderRadius: "10px",
    px: 3,
    py: 1,
    fontWeight: "medium",
    "&:hover": {
      backgroundColor: "#1EA8D9",
      transform: "translateY(-2px)",
    },
    transition: "all 0.3s ease",
  },
  contactButton: {
    backgroundColor: "transparent",
    color: "#25B2E8",
    border: "1px solid #25B2E8",
    borderRadius: "10px",
    px: 3,
    py: 1,
    fontWeight: "medium",
    "&:hover": {
      backgroundColor: "#f0f9ff",
    },
    transition: "all 0.3s ease",
  },
};

const PRICING_PLANS = [
  {
    plan: "Individual Free",
    teamSize: "1 User",
    price: { annually: "$0 forever", monthly: "$0 forever" },
    priceBody: {
      annually: "No card required",
      monthly: "No card required",
    },
    buttonLabel: "Get Started Free",
    registerUrl: register_free,
    trialLabel: "No credit card",
    documentsIncluded: "5 / month shared",
    templates: "Shared with docs",
    publicForms: "Shared with docs",
    branding: false,
    customWebAddress: false,
  },
  {
    plan: "Individual Pro",
    teamSize: "1 User",
    price: { annually: "$8.25/mo/user", monthly: "$12/user" },
    priceBody: {
      annually: "$99/user billed annually",
      monthly: "",
    },
    buttonLabel: "Get Started",
    registerUrl: register_professional,
    trialLabel: "FREE 30-Day Trial",
    documentsIncluded: "UNLIMITED",
    templates: "UNLIMITED",
    publicForms: "UNLIMITED",
    branding: true,
    customWebAddress: false,
  },
  {
    plan: "Small Business",
    teamSize: "2 Users",
    teamcapacity: "(minimum)",
    price: { annually: "$7.42/mo/user", monthly: "$11/user" },
    priceBody: {
      annually: "$89/user billed annually",
      monthly: "",
    },
    buttonLabel: "Get Started",
    registerUrl: register_team,
    trialLabel: "FREE 30-Day Trial",
    documentsIncluded: "UNLIMITED",
    templates: "UNLIMITED",
    publicForms: "UNLIMITED",
    branding: true,
    customWebAddress: "Subdomain",
  },
  {
    plan: "Enterprise",
    teamSize: "50 Users",
    teamcapacity: "(minimum)",
    price: { annually: "Contact Sales", monthly: "Contact Sales" },
    priceBody: {
      annually: "Custom pricing",
      monthly: "Custom pricing",
    },
    buttonLabel: "Contact Sales",
    documentsIncluded: "UNLIMITED",
    templates: "UNLIMITED",
    publicForms: "UNLIMITED",
    branding: true,
    customWebAddress: "Your Domain",
  },
  {
    plan: "On-Prem Hosting",
    teamSize: "50 Users",
    teamcapacity: "(minimum)",
    price: { annually: "Contact Sales", monthly: "Contact Sales" },
    priceBody: {
      annually: "Custom pricing",
      monthly: "Custom pricing",
    },
    buttonLabel: "Contact Sales",
    documentsIncluded: "UNLIMITED",
    templates: "UNLIMITED",
    publicForms: "UNLIMITED",
    branding: true,
    customWebAddress: "Your Domain",
  },
];

// Helper component for rendering feature values
const FeatureValue = ({ feature, plan, isAnnually }) => {
  if (feature === "teamSize") {
    return (
      <Box textAlign="center">
        <Typography sx={{ fontSize: "0.95rem", color: "#4F6169" }}>
          {plan.teamSize}
        </Typography>
        {plan.teamcapacity && (
          <Typography sx={{ fontSize: "0.8rem", color: "#4F6169" }}>
            {plan.teamcapacity}
          </Typography>
        )}
      </Box>
    );
  }

  if (feature === "price") {
    const currentPrice = isAnnually ? plan.price.annually : plan.price.monthly;
    const currentPriceBody = isAnnually
      ? plan.priceBody.annually
      : plan.priceBody.monthly;

    let mainText = isAnnually && currentPriceBody ? currentPriceBody : currentPrice;
    let subText = isAnnually && currentPriceBody ? currentPrice : currentPriceBody;

    // Keep Contact Sales as primary text in annual view (match monthly order).
    if (
      isAnnually &&
      currentPriceBody &&
      typeof currentPrice === "string" &&
      currentPrice.toLowerCase().includes("contact")
    ) {
      mainText = currentPrice;
      subText = currentPriceBody;
    }

    const isContactText =
      typeof mainText === "string" &&
      (mainText.toLowerCase().includes("contact") ||
        mainText.toLowerCase().includes("custom"));

    return (
      <Stack spacing={1} alignItems="center">
        {plan.priceBody1 && (
          <Typography sx={{ fontSize: "0.8rem", color: "red" }}>
            ({plan.priceBody1})
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: "1.15rem",
            color: "#1662A7",
            fontWeight: "bold",
            textTransform: isContactText ? "uppercase" : "none",
            letterSpacing: isContactText ? "0.04em" : "normal",
          }}
        >
          {mainText}
        </Typography>
        {subText && (
          <Typography
            sx={{
              fontSize: "0.85rem",
              color: "#4F6169",
            }}
          >
            {subText}
          </Typography>
        )}
      </Stack>
    );
  }

  if (feature === "branding") {
    return plan.branding ? (
      <CheckCircle sx={{ color: "#6ACE0C" }} />
    ) : (
      <Cancel color="error" />
    );
  }

  if (feature === "customWebAddress") {
    return plan.customWebAddress ? (
      <Typography sx={{ fontSize: "0.95rem", color: "#4F6169" }}>
        {plan.customWebAddress}
      </Typography>
    ) : (
      <Cancel color="error" />
    );
  }

  return (
    <Typography sx={{ fontSize: "0.95rem", color: "#4F6169" }}>
      {plan[feature]}
    </Typography>
  );
};

// Mobile card component for individual plans
const MobilePlanCard = ({ plan, isAnnually, onButtonClick }) => (
  <Box sx={styles.mobileCard}>
    <Box sx={{ p: 3, backgroundColor: "#F8F9FA" }}>
      <Typography sx={styles.tableHeader}>{plan.plan}</Typography>
    </Box>
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontWeight: "bold", color: "#4F6169", fontSize: "0.9rem" }}
          >
            TEAM SIZE
          </Typography>
          <FeatureValue
            feature="teamSize"
            plan={plan}
            isAnnually={isAnnually}
          />
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontWeight: "bold", color: "#4F6169", fontSize: "0.9rem" }}
          >
            PRICE
          </Typography>
          <FeatureValue feature="price" plan={plan} isAnnually={isAnnually} />
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontWeight: "bold", color: "#4F6169", fontSize: "0.9rem" }}
          >
            Documents Included
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", color: "#4F6169" }}>
            {plan.documentsIncluded}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontWeight: "bold", color: "#4F6169", fontSize: "0.9rem" }}
          >
            Templates
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", color: "#4F6169" }}>
            {plan.templates}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontWeight: "bold", color: "#4F6169", fontSize: "0.9rem" }}
          >
            Public Forms
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", color: "#4F6169" }}>
            {plan.publicForms}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontWeight: "bold", color: "#4F6169", fontSize: "0.9rem" }}
          >
            Branding
          </Typography>
          <FeatureValue
            feature="branding"
            plan={plan}
            isAnnually={isAnnually}
          />
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontWeight: "bold", color: "#4F6169", fontSize: "0.9rem" }}
          >
            Custom Web Address
          </Typography>
          <FeatureValue
            feature="customWebAddress"
            plan={plan}
            isAnnually={isAnnually}
          />
        </Stack>
      </Stack>

      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Button
          onClick={() => onButtonClick(plan)}
          sx={
            plan.buttonLabel === "Get Started"
              ? styles.actionButton
              : styles.contactButton
          }
        >
          {plan.buttonLabel}
        </Button>
        {plan.trialLabel && (
          <Typography
            sx={{
              color: "#00BE00",
              fontWeight: "bold",
              fontSize: "0.9rem",
              mt: 1,
            }}
          >
            {plan.trialLabel}
          </Typography>
        )}
      </Box>
    </Box>
  </Box>
);

function PricingAndPlans() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("annually");

  const isAnnually = activeTab === "annually";
  const isKba = activeTab === "kba";

  const handleButtonClick = (plan) => {
    if (plan.buttonLabel === "Contact Sales") {
      navigate("/2024/contact");
      return;
    }
    if (plan.registerUrl) {
      window.open(plan.registerUrl, "_blank");
      return;
    }
    if (
      plan.buttonLabel === "Get Started" ||
      plan.buttonLabel === "Get Started Free"
    ) {
      window.open(`${login}`, "_blank");
    }
  };

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          mt: { xs: 8, md: 10 },
          pt: { xs: 6, md: 12 },
          pb: { xs: 4, md: 8 },
          backgroundImage: `linear-gradient(rgba(22, 98, 167, 0.1), rgba(37, 178, 232, 0.05)), url(${pricing_plans})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { xs: "35vh", md: "10vh" },
        }}
      >
        <Container maxWidth="lg" sx={styles.container}>
          <Typography variant="h1" sx={styles.heroTitle}>
            Pricing Plans
          </Typography>
        </Container>
      </Box>

      {/* Content Section */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg" sx={styles.container}>
          <Stack spacing={4}>
            <Typography sx={styles.subtitle}>
              UNLIMITED document volume{" "}
              <span style={{ color: "#6E6B7B", fontWeight: "normal" }}>
                with
              </span>{" "}
              <span style={styles.highlight}>
                simple and affordable pricing.
              </span>
            </Typography>

            <Typography sx={styles.winText}>
              <span style={{ color: "#6E6B7B" }}>You</span> WIN{" "}
              <span style={{ color: "#6E6B7B" }}>with</span> RequireSign.
            </Typography>

            {/* Toggle Buttons */}
            <Stack direction="row" spacing={2} justifyContent="center">
              {["annually", "monthly", "kba"].map((tab) => {
                const label =
                  tab === "annually"
                    ? "Annually"
                    : tab === "monthly"
                    ? "Monthly"
                    : "KBA Add-on";
                const isActive = activeTab === tab;
                return (
                  <Button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    sx={{
                      ...styles.toggleButton,
                      backgroundColor: isActive ? "#25B2E8" : "transparent",
                      color: isActive ? "white" : "#25B2E8",
                      border: isActive ? "none" : "1px solid #25B2E8",
                      "&:hover": {
                        backgroundColor: isActive ? "#25B2E8" : "#f0f9ff",
                      },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Stack>

            {/* Pricing Tables (Annually / Monthly) */}
            {!isKba && (
            <>
            {/* Desktop Table */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <TableContainer sx={styles.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={styles.tableCell} />
                      {PRICING_PLANS.map((plan, index) => (
                        <TableCell
                          key={index}
                          align="center"
                          sx={styles.tableCell}
                        >
                          <Typography sx={styles.tableHeader}>
                            {plan.plan}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        sx={{
                          ...styles.tableCell,
                          fontWeight: "bold",
                          color: "#4F6169",
                        }}
                      >
                        TEAM SIZE
                      </TableCell>
                      {PRICING_PLANS.map((plan, index) => (
                        <TableCell
                          key={index}
                          align="center"
                          sx={styles.tableCell}
                        >
                          <FeatureValue
                            feature="teamSize"
                            plan={plan}
                            isAnnually={isAnnually}
                          />
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{
                          ...styles.tableCell,
                          fontWeight: "bold",
                          color: "#4F6169",
                        }}
                      >
                        PRICE
                      </TableCell>
                      {PRICING_PLANS.map((plan, index) => (
                        <TableCell
                          key={index}
                          align="center"
                          sx={styles.tableCell}
                        >
                          <FeatureValue
                            feature="price"
                            plan={plan}
                            isAnnually={isAnnually}
                          />
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{
                          ...styles.tableCell,
                          fontWeight: "bold",
                          color: "#4F6169",
                        }}
                      >
                        Documents Included
                      </TableCell>
                      {PRICING_PLANS.map((plan, index) => (
                        <TableCell
                          key={index}
                          align="center"
                          sx={styles.tableCell}
                        >
                          <Typography
                            sx={{ fontSize: "0.95rem", color: "#4F6169" }}
                          >
                            {plan.documentsIncluded}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{
                          ...styles.tableCell,
                          fontWeight: "bold",
                          color: "#4F6169",
                        }}
                      >
                        Templates
                      </TableCell>
                      {PRICING_PLANS.map((plan, index) => (
                        <TableCell
                          key={index}
                          align="center"
                          sx={styles.tableCell}
                        >
                          <Typography
                            sx={{ fontSize: "0.95rem", color: "#4F6169" }}
                          >
                            {plan.templates}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{
                          ...styles.tableCell,
                          fontWeight: "bold",
                          color: "#4F6169",
                        }}
                      >
                        Public Forms
                      </TableCell>
                      {PRICING_PLANS.map((plan, index) => (
                        <TableCell
                          key={index}
                          align="center"
                          sx={styles.tableCell}
                        >
                          <Typography
                            sx={{ fontSize: "0.95rem", color: "#4F6169" }}
                          >
                            {plan.publicForms}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{
                          ...styles.tableCell,
                          fontWeight: "bold",
                          color: "#4F6169",
                        }}
                      >
                        Branding
                      </TableCell>
                      {PRICING_PLANS.map((plan, index) => (
                        <TableCell
                          key={index}
                          align="center"
                          sx={styles.tableCell}
                        >
                          <FeatureValue
                            feature="branding"
                            plan={plan}
                            isAnnually={isAnnually}
                          />
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{
                          ...styles.tableCell,
                          fontWeight: "bold",
                          color: "#4F6169",
                        }}
                      >
                        Custom Web Address
                      </TableCell>
                      {PRICING_PLANS.map((plan, index) => (
                        <TableCell
                          key={index}
                          align="center"
                          sx={styles.tableCell}
                        >
                          <FeatureValue
                            feature="customWebAddress"
                            plan={plan}
                            isAnnually={isAnnually}
                          />
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow>
                      <TableCell sx={styles.tableCell} />
                      {PRICING_PLANS.map((plan, index) => (
                        <TableCell
                          key={index}
                          align="center"
                          sx={styles.tableCell}
                        >
                          <Stack spacing={1} alignItems="center">
                            <Button
                              onClick={() => handleButtonClick(plan)}
                              sx={
                                plan.buttonLabel === "Get Started"
                                  ? styles.actionButton
                                  : styles.contactButton
                              }
                            >
                              {plan.buttonLabel}
                            </Button>
                            {plan.trialLabel && (
                              <Typography
                                sx={{
                                  color: "#00BE00",
                                  fontWeight: "bold",
                                  fontSize: "0.9rem",
                                }}
                              >
                                {plan.trialLabel}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Mobile Cards */}
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              <Grid container spacing={3}>
                {PRICING_PLANS.map((plan, index) => (
                  <Grid item xs={12} key={index}>
                    <MobilePlanCard
                      plan={plan}
                      isAnnually={isAnnually}
                      onButtonClick={handleButtonClick}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            </>
            )}

            {/* KBA Add-on Tab Content */}
            {isKba && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 800,
                  color: "#1662A7",
                  fontSize: { xs: "1.5rem", md: "2.3rem" },
                  textAlign: "center",
                  mb: 1,
                }}
              >
                KBA Add-On
              </Typography>

              <Box
                sx={{
                  maxWidth: "860px",
                  mx: "auto",
                  mb: 3,
                  p: { xs: 2, md: 2.5 },
                  borderRadius: "14px",
                  border: "1px solid rgba(37, 178, 232, 0.35)",
                  background:
                    "linear-gradient(135deg, rgba(22, 98, 167, 0.06) 0%, rgba(37, 178, 232, 0.1) 100%)",
                }}
              >
                {/* <Typography
                  sx={{
                    color: "#334E68",
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                    textAlign: "center",
                    lineHeight: 1.7,
                  }}
                >
                  If you are an <b>accountant</b> and want to have <b>KBA</b>,
                  we provide this as an <b>add-on</b>.<br></br> <b>KBA</b> is required by
                  the <b>IRS</b> for identity verification.
                </Typography> */}
              </Box>

              <TableContainer
                sx={{
                  border: "1px solid #D9E2EC",
                  borderRadius: "16px",
                  boxShadow: "0px 8px 24px rgba(22, 98, 167, 0.08)",
                  overflow: "hidden",
                  maxWidth: "780px",
                  mx: "auto",
                  mb: 1,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          background:
                            "linear-gradient(90deg, rgba(22, 98, 167, 0.08), rgba(37, 178, 232, 0.08))",
                          borderBottom: "1px solid #D9E2EC",
                          py: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#1662A7",
                            fontWeight: 800,
                            fontSize: { xs: "1rem", md: "1.1rem" },
                          }}
                        >
                          KBA Credits
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          background:
                            "linear-gradient(90deg, rgba(22, 98, 167, 0.08), rgba(37, 178, 232, 0.08))",
                          borderBottom: "1px solid #D9E2EC",
                          py: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#1662A7",
                            fontWeight: 800,
                            fontSize: { xs: "1rem", md: "1.1rem" },
                          }}
                        >
                          Price per KBA
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: "#F8FBFF" } }}>
                      <TableCell sx={{ borderBottom: "1px solid #E6EDF5", py: 1.8, fontWeight: 600, color: "#334E68" }}>
                        100 - 250
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ borderBottom: "1px solid #E6EDF5", py: 1.8, fontWeight: 700, color: "#1662A7" }}
                      >
                        $2.49
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ "&:nth-of-type(even)": { backgroundColor: "#FFFFFF" } }}>
                      <TableCell sx={{ borderBottom: "1px solid #E6EDF5", py: 1.8, fontWeight: 600, color: "#334E68" }}>
                        251 - 500
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ borderBottom: "1px solid #E6EDF5", py: 1.8, fontWeight: 700, color: "#1662A7" }}
                      >
                        $2.29
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: "#F8FBFF" } }}>
                      <TableCell sx={{ borderBottom: "1px solid #E6EDF5", py: 1.8, fontWeight: 600, color: "#334E68" }}>
                        501 - 1000
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ borderBottom: "1px solid #E6EDF5", py: 1.8, fontWeight: 700, color: "#1662A7" }}
                      >
                        $1.99
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ "&:nth-of-type(even)": { backgroundColor: "#FFFFFF" } }}>
                      <TableCell sx={{ borderBottom: "1px solid #E6EDF5", py: 1.8, fontWeight: 600, color: "#334E68" }}>
                        1001 - 5000
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ borderBottom: "1px solid #E6EDF5", py: 1.8, fontWeight: 700, color: "#1662A7" }}
                      >
                        $1.79
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: "#F8FBFF" }}>
                      <TableCell sx={{ py: 2, fontWeight: 700, color: "#102A43" }}>
                        5000+
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2, fontWeight: 800, color: "#0F7EA5" }}>
                        $1.59
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            )}

            {/* Email Signup */}
            <Box sx={{ mt: { xs: 6, md: 8 } }}>
              <Textfield showPricingInfo={false} />
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
}

export default PricingAndPlans;
